import { Provider } from 'react-redux';
import React from 'react';
import ReactDOM from 'react-dom';
import 'reflect-metadata';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import * as serviceWorker from './serviceWorker';

import App from 'src/presentation/components/App';
import AppContext from 'src/presentation/contexts/AppContext';
import Scheduler from './adapters/Scheduler';
import agentActivityResource from './actions/agent/agentActivityResource';
import analytics from '@ad-scripts/lib/analytics';
import getAnalyticsAdapters from '@ad-scripts/lib/analytics/get_analytics_adapters';
import createBackend from 'src/infrastructure/createBackend';
import createHttpClient from 'src/infrastructure/createHttpClient';
import createMqttClient from 'src/infrastructure/createMqttClient';
import createReduxStore from 'src/configuration/createReduxStore';
import createStores from 'src/configuration/createStores';
import generateId from 'src/lib/generateId';
import history from 'src/configuration/history';
import initializeApp from 'src/actions/initialize/initializeApp';
import setupLocalAuth from 'src/infrastructure/setupLocalAuth';
import { AUTH_STORAGE_KEY } from 'src/adapters/AuthStore';
import { AuthUtils } from './models/Auth';
import { configureAgentActivity } from 'src/configuration/configureAgentActivity';
import { configureGatewayObservers } from 'src/configuration/configureGatewayObservers';
import { configureGateways } from 'src/configuration/configureGateways';
import { refreshToken } from './actions/auth/authSlice';

import 'setimmediate';

const config = window['gladlyConfig'] || {};
const sentryDsn = config.sentryDsn;

if (sentryDsn) {
  Sentry.init({
    release: config.release,
    environment: config.environment,
    dsn: sentryDsn,
    autoSessionTracking: true,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.0,
  });
}

const requestorId = generateId();
// eslint-disable-next-line
const useFakeBackend = process.env.REACT_APP_USE_LOCAL_BACKEND === 'true';

const httpClient = createHttpClient(requestorId);
const backend = createBackend({
  httpClient,
  mqttClient: createMqttClient(requestorId),
  requestorId,
});
const gateways = configureGateways(backend);
const stores = createStores(); // Note - NOT redux stores.

const appContext = {
  backend,
  gateways,
  stores,
};

export const store = createReduxStore(appContext);
configureGatewayObservers(gateways, { dispatch: store.dispatch, getState: store.getState });
configureAgentActivity({ dispatch: store.dispatch });

// For local backend we just write to local storage the necessary claims
if (useFakeBackend && !localStorage.getItem(AUTH_STORAGE_KEY)) {
  setupLocalAuth();
}

if (!analytics.isConfigured()) {
  const analyticsAdapters = getAnalyticsAdapters(config);
  analytics.configure(analyticsAdapters);
}

const { add } = agentActivityResource.effects;
history.listen(({ pathname }) => {
  analytics.page(window.location);
  store.dispatch(add({ page: pathname }));
});

store.dispatch(initializeApp());

const scheduler = new Scheduler(store.dispatch);
scheduler.executeEvery({
  period: AuthUtils.getRefreshDurationMS(store.getState().auth),
  id: 'refreshAuthToken',
  action: refreshToken,
});

ReactDOM.render(
  <Provider store={store}>
    <AppContext.Provider value={appContext}>
      <App />
    </AppContext.Provider>
  </Provider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

// TODO: temporary solution to add PS team possibility to use some feature
// in the admin panel. Remove this after the feature is available for all users.
// SC https://app.shortcut.com/gladly/story/209318/ad-remove-local-storage-check-for-showlasagnapage-feature-flag-after-feature-will-be-available-for-users
const ADMIN_SIDEKICK_FEATURE_KEY = 'feature_showLasagnaPage';
(window as any).allowLasagnaPage = (enable: boolean) => {
  if (!enable) {
    window.localStorage.removeItem(ADMIN_SIDEKICK_FEATURE_KEY);
    return;
  }
  window.localStorage.setItem(ADMIN_SIDEKICK_FEATURE_KEY, 'true');
};
